import { MEDIA_LIBRARY_URL_REGEX } from '../Constants';

/**
 * Helper function to check if we're running in local development
 * @returns {boolean}
 */
const isLocalDevelopment = () => {
    const localHosts = ['localhost', '127.0.0.1', '::1', 'local.paycor.com'];
    return localHosts.includes(window.location.hostname);
};

/**
 * Helper function to check if a URL is a media library URL
 * @param {string} url - The URL to check
 * @returns {boolean}
 */
const isMediaLibraryUrl = (url) => {
    if (!url) return false;
    // Test the URL against the media library regex pattern
    // Reset lastIndex to avoid issues with global flag
    MEDIA_LIBRARY_URL_REGEX.lastIndex = 0;
    return MEDIA_LIBRARY_URL_REGEX.test(url);
};

/**
 * Converts an absolute media URL to a relative path for local development
 * This allows the webpack dev server proxy to intercept the request
 * @param {string} url - The absolute URL
 * @returns {string} - The relative path or original URL
 */
const convertToRelativePathForLocal = (url) => {
    if (!isLocalDevelopment()) {
        return url;
    }

    try {
        const urlObj = new URL(url);
        // Convert full URLs to relative paths so the proxy can intercept them
        // e.g., https://hcm-quarterly.paycor.com/paths/paths-sls-api-v2/media/file/123 
        //    -> /paths/paths-sls-api-v2/media/file/123
        if (urlObj.pathname.startsWith('/paths/')) {
            return urlObj.pathname + urlObj.search;
        }
        return url;
    } catch (e) {
        // If URL parsing fails, return original
        return url;
    }
};

/**
 * Fetches a signed S3 URL from the media API endpoint
 * @param {string} mediaUrl - The media library URL
 * @param {string} token - The JWT authentication token from Redux state
 * @returns {Promise<string>} - The signed S3 URL or original URL if fetch fails
 */
const fetchSignedUrl = async (mediaUrl, token) => {
    if (!mediaUrl || !token) {
        return mediaUrl;
    }

    try {
        // Convert to relative path for local development to avoid CORS issues
        const urlForFetch = convertToRelativePathForLocal(mediaUrl);
        
        // Make request to media endpoint with returnLink=true to get signed URL
        const urlWithParam = `${urlForFetch}?returnLink=true`;
        const response = await fetch(urlWithParam, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            console.warn(`Failed to fetch signed URL for ${mediaUrl}: ${response.status}`);
            return mediaUrl;
        }

        // The API returns the signed URL as plain text
        const signedUrl = await response.text();
        return signedUrl || mediaUrl;
    } catch (error) {
        console.warn(`Error fetching signed URL for ${mediaUrl}:`, error);
        return mediaUrl;
    }
};

/**
 * Replaces media library URLs with signed S3 URLs for audio, video, and anchor elements.
 * This ensures media files are accessed with proper authentication via signed URLs.
 * 
 * Targets:
 * - <audio src="..."> elements
 * - <video src="..."> elements
 * - <source src="..."> elements inside audio/video tags
 * - <a href="..."> elements linking to media files
 * 
 * @param {HTMLElement} containerElement - The container element to search for media tags
 * @param {string} token - The JWT authentication token from Redux state (state.auth.idToken)
 * @returns {Promise<void>}
 */
export const injectMediaAuthToken = async (containerElement, token) => {
    if (!containerElement) {
        return;
    }
    
    if (!token) {
        // No token available, skip authentication
        return;
    }

    const urlReplacementPromises = [];

    // Find and update audio and video elements with direct src attributes
    const mediaElements = containerElement.querySelectorAll('audio[src], video[src]');
    mediaElements.forEach((element) => {
        const src = element.getAttribute('src');
        
        // Only process media library URLs
        if (src && isMediaLibraryUrl(src)) {
            const promise = fetchSignedUrl(src, token).then((signedUrl) => {
                if (signedUrl !== src) {
                    element.setAttribute('src', signedUrl);
                }
            });
            urlReplacementPromises.push(promise);
        }
    });

    // Find and update source elements inside audio/video tags
    const sourceElements = containerElement.querySelectorAll('audio source, video source');
    sourceElements.forEach((element) => {
        const src = element.getAttribute('src');
        
        // Only process media library URLs
        if (src && isMediaLibraryUrl(src)) {
            const promise = fetchSignedUrl(src, token).then((signedUrl) => {
                if (signedUrl !== src) {
                    element.setAttribute('src', signedUrl);
                    
                    // Reload the parent media element to pick up the new source URL
                    const parentElement = element.parentElement;
                    if (parentElement && (parentElement.tagName === 'AUDIO' || parentElement.tagName === 'VIDEO')) {
                        try {
                            parentElement.load();
                        } catch (e) {
                            // HTMLMediaElement.load() may not be implemented in test environments
                            // This is safe to ignore as it's just a progressive enhancement
                        }
                    }
                }
            });
            urlReplacementPromises.push(promise);
        }
    });

    // Find and update anchor links that point to media library files
    const anchorElements = containerElement.querySelectorAll('a[href]');
    anchorElements.forEach((element) => {
        const href = element.getAttribute('href');
        
        // Only process media library URLs
        if (href && isMediaLibraryUrl(href)) {
            const promise = fetchSignedUrl(href, token).then((signedUrl) => {
                if (signedUrl !== href) {
                    element.setAttribute('href', signedUrl);
                }
            });
            urlReplacementPromises.push(promise);
        }
    });

    // Wait for all URL replacements to complete
    await Promise.all(urlReplacementPromises);
};

/**
 * Sets up a MutationObserver to automatically replace media URLs with signed URLs when new media elements
 * are added to the DOM dynamically.
 * 
 * Note: This requires access to the token which must be passed when media is added.
 * In most cases, component lifecycle methods (componentDidUpdate) should handle this.
 * 
 * @param {HTMLElement} rootElement - The root element to observe for changes
 * @param {string} token - The JWT authentication token from Redux state
 * @returns {MutationObserver} The observer instance (can be disconnected if needed)
 */
export const setupMediaAuthObserver = (rootElement, token) => {
    if (!rootElement || typeof MutationObserver === 'undefined' || !token) {
        return null;
    }

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                // Only process element nodes
                if (node.nodeType === Node.ELEMENT_NODE) {
                    // Check if the added node itself is a media element
                    if (node.tagName === 'AUDIO' || node.tagName === 'VIDEO') {
                        injectMediaAuthToken(node.parentElement, token).catch((error) => {
                            console.warn('Error injecting media auth tokens in observer:', error);
                        });
                    } else {
                        // Check if the added node contains media elements
                        injectMediaAuthToken(node, token).catch((error) => {
                            console.warn('Error injecting media auth tokens in observer:', error);
                        });
                    }
                }
            });
        });
    });

    observer.observe(rootElement, {
        childList: true,
        subtree: true
    });

    return observer;
};
